import { Button, Chip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useState, useRef } from "react";
import ToolPageLayout from "../../components/tools/ToolPageLayout";
import SignUpPromptModal from "../../components/tools/SignUpPromptModal";

const MeetingNotes: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("summary");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const outputFormats = [
    { id: "summary", label: "Meeting Summary", icon: "lucide:file-text" },
    { id: "action", label: "Action Items", icon: "lucide:check-square" },
    { id: "full", label: "Full Notes", icon: "lucide:clipboard-list" },
    { id: "email", label: "Email Recap", icon: "lucide:mail" },
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleGenerate = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setShowSignUpModal(true);
    }, 2500);
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI Meeting Notes Generator - YouWo AI",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Convert meeting recordings into organized notes with action items, decisions, and key takeaways. Free AI-powered meeting notes generator.",
  };

  const howItWorksSteps = [
    {
      title: "Upload Recording",
      description: "Upload your meeting recording or record live directly in your browser.",
    },
    {
      title: "AI Processes",
      description: "Our AI transcribes, identifies speakers, and extracts key information.",
    },
    {
      title: "Get Organized Notes",
      description: "Receive structured meeting notes with action items and decisions.",
    },
  ];

  const benefits = [
    {
      title: "100+ Languages",
      description: "Transcribe meetings in over 100 languages with automatic detection.",
      icon: "lucide:globe",
    },
    {
      title: "Clickable Timestamps",
      description: "Jump to any part of the meeting with clickable timestamps.",
      icon: "lucide:clock",
    },
    {
      title: "Action Item Extraction",
      description: "AI automatically identifies and lists action items with assignees.",
      icon: "lucide:check-square",
    },
    {
      title: "Speaker Identification",
      description: "Automatically detects and labels different speakers throughout.",
      icon: "lucide:users",
    },
    {
      title: "Decision Tracking",
      description: "Highlights key decisions made during the meeting.",
      icon: "lucide:gavel",
    },
    {
      title: "Email Ready",
      description: "Generate professional meeting recap emails in one click.",
      icon: "lucide:mail",
    },
  ];

  const faqs = [
    {
      question: "What is the Meeting Notes Generator?",
      answer: "The Meeting Notes Generator is a free AI tool that converts meeting recordings into organized, professional notes. It extracts action items, decisions, and key points automatically.",
    },
    {
      question: "Can it identify different speakers?",
      answer: "Yes! Our AI automatically detects and labels different speakers, making it easy to see who said what during the meeting.",
    },
    {
      question: "What formats are supported?",
      answer: "We support all major audio and video formats including MP3, MP4, WAV, M4A, WEBM, and more. You can also record meetings directly in your browser.",
    },
    {
      question: "Can I edit the generated notes?",
      answer: "Absolutely! All generated notes can be edited, reorganized, and enhanced. You can also add your own comments and annotations.",
    },
    {
      question: "Is there an email recap feature?",
      answer: "Yes! With one click, you can generate a professional email recap of the meeting that you can send directly to attendees.",
    },
    {
      question: "Does it work with video conferencing tools?",
      answer: "You can upload recordings from Zoom, Teams, Google Meet, or any other conferencing tool. We're also working on direct integrations.",
    },
  ];

  const relatedTools = [
    {
      name: "AI Audio Transcriber",
      description: "Convert any audio file to accurate text with speaker detection.",
      href: "/tools/audio-transcriber",
      icon: "lucide:mic",
      iconColor: "text-blue-500",
    },
    {
      name: "Lecture Notes AI",
      description: "Transform lecture recordings into comprehensive study notes.",
      href: "/tools/lecture-notes",
      icon: "lucide:graduation-cap",
      iconColor: "text-emerald-500",
    },
    {
      name: "YouTube Summarizer",
      description: "Transform YouTube videos into concise summaries with timestamps.",
      href: "/tools/youtube-summarizer",
      icon: "lucide:youtube",
      iconColor: "text-red-500",
    },
    {
      name: "PDF Summarizer",
      description: "Turn lengthy PDFs and documents into clear, concise summaries.",
      href: "/tools/pdf-summarizer",
      icon: "lucide:file-text",
      iconColor: "text-orange-500",
    },
  ];

  return (
    <>
      <ToolPageLayout
        title="Free AI Meeting Notes Generator - Auto Notes & Action Items | YouWo AI"
        metaDescription="Convert meeting recordings into organized notes with action items, decisions, and key takeaways automatically. Free AI-powered meeting notes generator."
        keywords="meeting notes, meeting summary, action items, meeting minutes, ai meeting notes, zoom transcription, teams meeting notes"
        canonicalUrl="https://youwo.ai/tools/meeting-notes"
        structuredData={structuredData}
        heroTitle="Meeting Notes Generator"
        heroSubtitle="Convert meetings into organized notes with clickable timestamps, action items, and key decisions. Supports 100+ languages. Never miss a detail."
        heroIcon="lucide:users"
        heroIconColor="text-indigo-500"
        heroIconBg="bg-indigo-50"
        howItWorksSteps={howItWorksSteps}
        benefitsTitle="Why Choose YouWo AI Meeting Notes?"
        benefits={benefits}
        faqs={faqs}
        relatedTools={relatedTools}
      >
        {/* Tool Input Area */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
          {/* Record Button */}
          <div className="flex justify-center mb-6">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                isRecording
                  ? "bg-red-500 animate-pulse"
                  : "bg-gradient-to-br from-indigo-500 to-indigo-600 hover:shadow-lg"
              }`}
              onClick={() => {
                if (isRecording) {
                  setHasRecorded(true);
                }
                setIsRecording(!isRecording);
              }}
            >
              <Icon
                icon={isRecording ? "lucide:square" : "lucide:mic"}
                className="text-white text-4xl"
              />
            </motion.button>
          </div>

          <p className="text-center text-gray-500 mb-6">
            {isRecording
              ? "Recording meeting... Click to stop"
              : hasRecorded
                ? "Recording ready! Click Generate to continue"
                : "Click to start recording your meeting"}
          </p>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">or upload a recording</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
              isDragging
                ? "border-indigo-500 bg-indigo-50"
                : selectedFile
                ? "border-green-400 bg-green-50"
                : "border-gray-200 hover:border-indigo-500"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="audio/*,video/*"
              onChange={handleFileSelect}
            />

            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <Icon icon="lucide:file-video" className="text-green-600 text-xl" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  variant="light"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Icon icon="lucide:upload-cloud" className="text-gray-400 text-3xl" />
                <p className="text-gray-600">
                  Drag & drop meeting recording
                </p>
                <p className="text-xs text-gray-400">
                  MP3, MP4, WAV, M4A, WEBM supported
                </p>
              </div>
            )}
          </div>

          {/* Output Format Selector */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-6">
            <span className="text-sm text-gray-600 font-medium">Output Format:</span>
            <div className="flex flex-wrap gap-2">
              {outputFormats.map((format) => (
                <Chip
                  key={format.id}
                  variant={selectedFormat === format.id ? "solid" : "bordered"}
                  color={selectedFormat === format.id ? "primary" : "default"}
                  className={`cursor-pointer transition-all ${
                    selectedFormat === format.id
                      ? "bg-indigo-500 text-white"
                      : "border-gray-200 text-gray-600 hover:border-indigo-500"
                  }`}
                  startContent={<Icon icon={format.icon} className="text-sm" />}
                  onClick={() => setSelectedFormat(format.id)}
                >
                  {format.label}
                </Chip>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6">
            <Button
              color="primary"
              size="lg"
              className="w-full bg-indigo-500 hover:bg-indigo-600 h-14 font-semibold"
              isLoading={isLoading}
              isDisabled={!selectedFile && !isRecording && !hasRecorded}
              onClick={handleGenerate}
            >
              {isLoading ? "Processing Meeting..." : "Generate Meeting Notes"}
            </Button>
          </div>
        </div>
      </ToolPageLayout>

      <SignUpPromptModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        toolName="Meeting Notes"
      />
    </>
  );
};

export default MeetingNotes;
